import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import toast from 'react-hot-toast';
import {
  Box, Typography, Card, CardContent, TextField, InputAdornment, Table, TableBody,
  TableCell, TableContainer, TableHead, TableRow, Paper, Chip, Avatar, Button,
  Dialog, DialogTitle, DialogContent, DialogActions, IconButton, Tooltip,
  CircularProgress, Autocomplete,
  Stack,
} from '@mui/material';
import {
  Search as SearchIcon, People as PeopleIcon, Add as AddIcon,
  PersonAdd as AssignIcon,
  PersonOff as DeactivateIcon,
  Restore as ReactivateIcon,
  DeleteForever as DeleteForeverIcon,
  ForwardToInbox as ResendIcon,
  Share as ShareIcon,
  UploadFile as ImportIcon,
} from '@mui/icons-material';

import PatientSharingSettings from '../components/PatientSharingSettings';
import PatientImportDialog from '../components/PatientImportDialog';
import { useAuth } from '../contexts/AuthContext';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

export default function ManagePatients() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isClinicAdmin = user?.role === 'clinic_admin';
  const [sharingOpen, setSharingOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [patients, setPatients] = useState([]);
  const [filtered, setFiltered] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  // Registration date range. Empty means unbounded on that side, so one box alone is a
  // valid filter — "everything since Monday" is the common ask, not a closed range.
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [loading, setLoading] = useState(true);
  /** Providers + clinic admins who may receive patient assignments */
  const [clinicians, setClinicians] = useState([]);

  // Create dialog
  const [createOpen, setCreateOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [assigning, setAssigning] = useState(false);
  const [rowBusy, setRowBusy] = useState(null); // { id, action } while a row action runs
  const [createForm, setCreateForm] = useState({ full_name: '', email: '', phone: '', reason: '' });

  // Assign dialog
  const [assignOpen, setAssignOpen] = useState(false);
  const [assignPatient, setAssignPatient] = useState(null);
  const [assignProviderId, setAssignProviderId] = useState('');

  // Per-row resend-invite in-flight state
  const [resendingId, setResendingId] = useState(null);

  const loadPatients = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/v1/admin/patients`);
      setPatients(res.data.patients);
      setFiltered(res.data.patients);
    } catch { toast.error('Failed to load patients'); }
    finally { setLoading(false); }
  };

  const loadAssignables = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/v1/admin/assignable-clinicians`);
      setClinicians(res.data.clinicians || []);
    } catch {
      toast.error('Failed to load clinicians for assignment');
      setClinicians([]);
    }
  };

  useEffect(() => {
    loadPatients();
    loadAssignables();
  }, []);

  useEffect(() => {
    const q = searchTerm.trim().toLowerCase();
    // Compared on the calendar date, not the timestamp: `to` has to include everything
    // that happened ON that day, and 'YYYY-MM-DD' strings order correctly as text.
    const inRange = (p) => {
      const d = (p.created_at || '').slice(0, 10);
      if (!d) return !fromDate && !toDate;   // undated rows only survive an unfiltered view
      if (fromDate && d < fromDate) return false;
      if (toDate && d > toDate) return false;
      return true;
    };
    setFiltered(patients.filter((p) => {
      if (!inRange(p)) return false;
      if (!q) return true;
      return (p.full_name || '').toLowerCase().includes(q)
          || (p.email || '').toLowerCase().includes(q);
    }));
  }, [searchTerm, patients, fromDate, toDate]);

  const handleCreate = async () => {
    if (creating) return;
    setCreating(true);
    try {
      const res = await axios.post(`${API_URL}/api/v1/admin/patients`, createForm);
      toast.success(res.data?.status === 'linked'
        ? 'Patient linked — notification sent to their account'
        : 'Patient registered — invite sent to set up their account');
      setCreateOpen(false);
      setCreateForm({ full_name: '', email: '', phone: '', reason: '' });
      loadPatients();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Registration failed');
    } finally {
      setCreating(false);
    }
  };

  const handleAssign = async () => {
    // An empty selection is a real instruction — "no clinician" — not a no-op. Guarding
    // on it is what made an assignment impossible to undo once it had been made.
    if (!assignPatient || assigning) return;
    const clearing = !assignProviderId;
    setAssigning(true);
    try {
      await axios.put(`${API_URL}/api/v1/admin/patients/${assignPatient.id}/assign-provider`, {
        provider_id: assignProviderId || null,
      });
      toast.success(clearing ? 'Patient is no longer assigned to a doctor'
                             : 'Patient assigned to doctor');
      setAssignOpen(false);
      loadPatients();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Assignment failed');
    } finally {
      setAssigning(false);
    }
  };

  const handleDeactivatePatient = async (p) => {
    if (rowBusy || !window.confirm(`Deactivate record for ${p.full_name}? They will be hidden from clinical workflows until reactivated.`)) return;
    setRowBusy({ id: p.id, action: 'deactivate' });
    try {
      await axios.delete(`${API_URL}/api/v1/admin/patients/${p.id}`);
      toast.success('Patient deactivated');
      loadPatients();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to deactivate');
    } finally {
      setRowBusy(null);
    }
  };

  const handleReactivatePatient = async (p) => {
    if (rowBusy || !window.confirm(`Reactivate record for ${p.full_name}?`)) return;
    setRowBusy({ id: p.id, action: 'reactivate' });
    try {
      await axios.put(`${API_URL}/api/v1/admin/patients/${p.id}`, { is_active: true });
      toast.success('Patient reactivated');
      loadPatients();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to reactivate');
    } finally {
      setRowBusy(null);
    }
  };

  const handleResendInvite = async (p) => {
    if (!p.email) {
      toast.error('Patient has no email on file');
      return;
    }
    setResendingId(p.id);
    try {
      const res = await axios.post(`${API_URL}/api/v1/admin/patients/${p.id}/resend-invite`);
      const dispatched = res.data?.invite_email_dispatched;
      const linked = res.data?.status === 'linked';
      if (dispatched) {
        toast.success(linked ? 'Login reminder sent' : 'Invite re-sent');
      } else {
        toast(
          linked
            ? 'Login reminder queued (SMTP not configured — see server logs)'
            : 'Invite created (SMTP not configured — see server logs)',
          { icon: '!' }
        );
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to resend invite');
    } finally {
      setResendingId(null);
    }
  };

  const handlePermanentDeletePatient = async (p) => {
    if (rowBusy || !window.confirm(
        `Permanently delete ${p.full_name} and all visit notes for this patient at your clinic?\n\nThis cannot be undone.`,
      )) {
      return;
    }
    setRowBusy({ id: p.id, action: 'delete' });
    try {
      await axios.delete(`${API_URL}/api/v1/admin/patients/${p.id}/permanent`);
      toast.success('Patient permanently deleted');
      loadPatients();
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : 'Failed to delete (admin only, or check server logs)');
    } finally {
      setRowBusy(null);
    }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Avatar sx={{ bgcolor: 'secondary.main', width: 48, height: 48 }}><PeopleIcon /></Avatar>
          <Box>
            <Typography variant="h4" fontWeight={700}>Manage Patients</Typography>
            <Typography variant="body2" color="text.secondary">
              Register patients and assign them to a provider or clinic admin (staff and admins can assign)
            </Typography>
          </Box>
        </Box>
        <Stack direction="row" spacing={1}>
          {/* Sharing is the office admin's decision; the same switches live in Settings. */}
          {isClinicAdmin && (
            <Button variant="outlined" startIcon={<ShareIcon />} onClick={() => setSharingOpen(true)}>
              Patient sharing
            </Button>
          )}
          <Button variant="outlined" startIcon={<ImportIcon />} onClick={() => setImportOpen(true)}>Import patients</Button>
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => setCreateOpen(true)}>Register Patient</Button>
        </Stack>
      </Box>

      <PatientImportDialog open={importOpen} onClose={() => setImportOpen(false)} onImported={loadPatients} />

      <Dialog open={sharingOpen} onClose={() => setSharingOpen(false)} maxWidth="sm" fullWidth>
        <DialogContent sx={{ pb: 0 }}>
          <PatientSharingSettings />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setSharingOpen(false)}>Done</Button>
        </DialogActions>
      </Dialog>

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ md: 'center' }}>
            <TextField fullWidth placeholder="Search by name or email…" value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon color="action" /></InputAdornment> }}
            />
            <TextField
              type="date" label="Registered from" size="small"
              InputLabelProps={{ shrink: true }} inputProps={{ max: toDate || undefined }}
              value={fromDate} onChange={(e) => setFromDate(e.target.value)}
              sx={{ minWidth: 170 }}
            />
            <TextField
              type="date" label="to" size="small"
              InputLabelProps={{ shrink: true }} inputProps={{ min: fromDate || undefined }}
              value={toDate} onChange={(e) => setToDate(e.target.value)}
              sx={{ minWidth: 150 }}
            />
            {(fromDate || toDate || searchTerm) && (
              <Button size="small" onClick={() => { setFromDate(''); setToDate(''); setSearchTerm(''); }}>
                Clear
              </Button>
            )}
          </Stack>
          <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
            Showing {filtered.length} of {patients.length} patients, newest first.
          </Typography>
        </CardContent>
      </Card>

      <TableContainer component={Paper} variant="outlined">
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>Registered</TableCell>
              <TableCell>Email</TableCell>
              <TableCell>Account</TableCell>
              <TableCell>Assigned Doctor</TableCell>
              <TableCell>Reason</TableCell>
              <TableCell align="center">Record</TableCell>
              <TableCell align="center">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={8}>Loading…</TableCell></TableRow>
            ) : filtered.length === 0 ? (
              <TableRow><TableCell colSpan={8}>No patients found.</TableCell></TableRow>
            ) : filtered.map((p) => (
              <TableRow key={p.id} hover sx={{ opacity: p.is_active === false ? 0.75 : 1 }}>
                <TableCell>
                  <Typography
                    component="button"
                    type="button"
                    fontWeight={600}
                    onClick={() => navigate('/patients', { state: { openPatientId: p.id } })}
                    sx={{
                      cursor: 'pointer',
                      border: 'none',
                      background: 'none',
                      padding: 0,
                      font: 'inherit',
                      color: 'primary.main',
                      textAlign: 'left',
                      textDecoration: 'underline',
                      '&:hover': { color: 'primary.dark' },
                    }}
                  >
                    {p.full_name}
                  </Typography>
                </TableCell>
                <TableCell>
                  {p.created_at ? (
                    <>
                      <Typography variant="body2">
                        {new Date(p.created_at).toLocaleDateString(undefined,
                          { day: 'numeric', month: 'short', year: 'numeric' })}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {new Date(p.created_at).toLocaleTimeString(undefined,
                          { hour: '2-digit', minute: '2-digit' })}
                      </Typography>
                    </>
                  ) : '—'}
                </TableCell>
                <TableCell>{p.email || '—'}</TableCell>
                <TableCell>
                  {p.has_account
                    ? <Chip size="small" label="Active" color="success" variant="outlined" />
                    : <Chip size="small" label="Invite sent" color="info" variant="outlined" />}
                </TableCell>
                <TableCell>
                  {p.provider_name || <Chip size="small" label="Unassigned" color="warning" />}
                </TableCell>
                <TableCell>{p.reason || '—'}</TableCell>
                <TableCell align="center">
                  {p.is_active === false ? (
                    <Chip size="small" label="Inactive" />
                  ) : (
                    <Chip size="small" label="Active" color="success" variant="outlined" />
                  )}
                </TableCell>
                <TableCell align="center">
                  <Tooltip title="Assign to Doctor">
                    <IconButton
                      size="small"
                      disabled={p.is_active === false}
                      onClick={() => {
                        setAssignPatient(p);
                        setAssignProviderId(p.provider_id || '');
                        setAssignOpen(true);
                      }}
                    >
                      <AssignIcon />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title={p.has_account ? 'Re-send login reminder email' : 'Re-send portal invite email'}>
                    <span>
                      <IconButton
                        size="small"
                        disabled={!p.email || p.is_active === false || resendingId === p.id || rowBusy?.id === p.id}
                        onClick={() => handleResendInvite(p)}
                      >
                        {resendingId === p.id ? <CircularProgress size={18} color="inherit" /> : <ResendIcon />}
                      </IconButton>
                    </span>
                  </Tooltip>
                  {p.is_active !== false ? (
                    <Tooltip title="Deactivate record">
                      <span>
                        <IconButton size="small" color="warning" disabled={rowBusy?.id === p.id} onClick={() => handleDeactivatePatient(p)}>
                          {rowBusy?.id === p.id && rowBusy?.action === 'deactivate' ? <CircularProgress size={18} color="inherit" /> : <DeactivateIcon />}
                        </IconButton>
                      </span>
                    </Tooltip>
                  ) : (
                    <Tooltip title="Reactivate record">
                      <span>
                        <IconButton size="small" color="success" disabled={rowBusy?.id === p.id} onClick={() => handleReactivatePatient(p)}>
                          {rowBusy?.id === p.id && rowBusy?.action === 'reactivate' ? <CircularProgress size={18} color="inherit" /> : <ReactivateIcon />}
                        </IconButton>
                      </span>
                    </Tooltip>
                  )}
                  {isClinicAdmin && (
                    <Tooltip title="Delete permanently (removes visits & notes)">
                      <span>
                        <IconButton size="small" color="error" disabled={rowBusy?.id === p.id} onClick={() => handlePermanentDeletePatient(p)}>
                          {rowBusy?.id === p.id && rowBusy?.action === 'delete' ? <CircularProgress size={18} color="inherit" /> : <DeleteForeverIcon />}
                        </IconButton>
                      </span>
                    </Tooltip>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Register Patient Dialog */}
      <Dialog open={createOpen} onClose={() => { if (!creating) setCreateOpen(false); }} maxWidth="sm" fullWidth>
        <DialogTitle>Register New Patient</DialogTitle>
        <DialogContent>
          <TextField fullWidth label="Full Name" value={createForm.full_name}
            onChange={(e) => setCreateForm({ ...createForm, full_name: e.target.value })} margin="normal" required />
          <TextField fullWidth label="Email" value={createForm.email}
            onChange={(e) => setCreateForm({ ...createForm, email: e.target.value })} margin="normal" required
            helperText="Patient will receive an invite to set up their own password" />
          <TextField fullWidth label="Phone" value={createForm.phone}
            onChange={(e) => setCreateForm({ ...createForm, phone: e.target.value })} margin="normal" />
          <TextField fullWidth label="Reason / Chief Complaint" value={createForm.reason}
            onChange={(e) => setCreateForm({ ...createForm, reason: e.target.value })} margin="normal" />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setCreateOpen(false)} disabled={creating}>Cancel</Button>
          <Button variant="contained" onClick={handleCreate}
            disabled={creating || !createForm.full_name || !createForm.email}
            startIcon={creating ? <CircularProgress size={16} color="inherit" /> : null}>
            {creating ? 'Registering…' : 'Register'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Assign to Doctor Dialog */}
      <Dialog open={assignOpen} onClose={() => { if (!assigning) setAssignOpen(false); }} maxWidth="xs" fullWidth>
        <DialogTitle>Assign to Doctor</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {assignPatient?.full_name}
          </Typography>
          {/* Searchable: with a large practice, scrolling a plain list for one doctor is
              the slow part. Matches name or email; clearing it means "no doctor". */}
          <Autocomplete
            options={clinicians}
            value={clinicians.find((c) => c.id === assignProviderId) || null}
            onChange={(e, v) => setAssignProviderId(v ? v.id : '')}
            getOptionLabel={(c) => c.full_name || c.email || ''}
            isOptionEqualToValue={(o, v) => o.id === v.id}
            filterOptions={(opts, { inputValue }) => {
              const q = inputValue.trim().toLowerCase();
              return q ? opts.filter((c) => `${c.full_name || ''} ${c.email || ''}`.toLowerCase().includes(q)) : opts;
            }}
            noOptionsText="No doctor matches that search"
            renderOption={(props, c) => (
              <Box component="li" {...props} key={c.id} sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography variant="body2" fontWeight={600} noWrap>{c.full_name}</Typography>
                  <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block' }}>{c.email}</Typography>
                </Box>
                <Chip size="small" label={c.role === 'clinic_admin' ? 'Clinic admin' : 'Provider'}
                  color={c.role === 'clinic_admin' ? 'warning' : 'default'} />
              </Box>
            )}
            renderInput={(params) => (
              <TextField {...params} margin="normal" label="Assign to"
                placeholder="Search by name or email"
                helperText="Includes providers and clinic administrators in your practice. Clear it to leave the patient unassigned." />
            )}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setAssignOpen(false)} disabled={assigning}>Cancel</Button>
          <Button variant="contained" onClick={handleAssign}
            disabled={assigning}
            startIcon={assigning ? <CircularProgress size={16} color="inherit" /> : null}>
            {assigning
              ? (assignProviderId ? 'Assigning…' : 'Removing…')
              : (assignProviderId ? 'Assign' : 'Remove doctor')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
